import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DomesticDeliveryMethod } from '~/types/general';
import type { Cart } from '~/graphql/graphql';

export const sellerDeliveryKey = (organizationId: string, facilityId: string) =>
  `${organizationId}__${facilityId}`;

export type QuantityOverride = {
  value: number;
  piecesCount?: number;
  settled?: boolean;
};

export type QuantityOverridesByCart = Record<
  string,
  Record<string, QuantityOverride>
>;

export type CartState = {
  carts: Cart[];
  quantityOverrides: QuantityOverridesByCart;
  cartTotalQuantity: number;
  selectedLocationId: string;
  deliveryMethodBySeller: Record<string, DomesticDeliveryMethod>;
  pendingQuantitySaveCount: number;
};

const initialState: CartState = {
  carts: [],
  quantityOverrides: {},
  cartTotalQuantity: 0,
  selectedLocationId: '',
  deliveryMethodBySeller: {},
  pendingQuantitySaveCount: 0,
};

type OverridableItem = {
  platformProductsLengths?: { id?: string | null } | null;
  quantity: { value: number };
};

/**
 * Merges a cart's pending quantity edits over its server items. Both cart
 * surfaces render through this, so an optimistic quantity shows up in exactly
 * one place regardless of where each surface reads its server items from.
 */
export function applyQuantityOverridesToItems<T extends OverridableItem>(
  items: T[],
  overrides?: Record<string, QuantityOverride>
): T[] {
  if (!overrides) return items;
  return items.map((item) => {
    const productId = item?.platformProductsLengths?.id;
    const override = productId ? overrides[productId] : undefined;
    if (!override) return item;
    const merged = {
      ...item,
      quantity: { ...item.quantity, value: override.value },
    } as T & { quantityStored?: { value: number } };
    if (override.piecesCount != null) {
      // quantityStored isn't on the base OrderItem type — it's a UI-local extra
      // field that the row total prices off, so an override that moved only the
      // quantity would leave the total showing the pre-edit figure.
      merged.quantityStored = { value: override.piecesCount };
    }
    return merged;
  });
}

/**
 * Drops the edits whose saves have landed — the server payload that just arrived
 * represents them. An unsettled edit stays: its save is still in flight, so the
 * payload still carries the pre-edit value.
 */
function dropSettledOverrides(overrides: QuantityOverridesByCart) {
  for (const [cartId, byProduct] of Object.entries(overrides)) {
    for (const [productId, override] of Object.entries(byProduct)) {
      if (override.settled) dropOverride(overrides, cartId, productId);
    }
  }
}

function dropOverride(
  overrides: QuantityOverridesByCart,
  cartId: string,
  productId: string
) {
  const byProduct = overrides[cartId];
  if (!byProduct?.[productId]) return;
  delete byProduct[productId];
  if (Object.keys(byProduct).length === 0) delete overrides[cartId];
}

const cartSlice = createSlice({
  name: 'carts',
  initialState,
  reducers: {
    /**
     * Server truth, so it replaces `carts` outright — but a quantity edit whose
     * save is still in flight is not represented there yet. Every successful
     * save invalidates the cart query, so one line's response refetches the
     * whole cart and would otherwise wipe a sibling line's unsaved edit back to
     * its pre-edit value while the buyer was still typing.
     */
    setCarts(state, action: PayloadAction<Cart[]>) {
      state.carts = action.payload;
      dropSettledOverrides(state.quantityOverrides);
    },
    /**
     * The same hand-back for a surface that holds its own server carts: the cart
     * page renders its query result directly, so nothing writes `carts` for it.
     */
    dropSettledQuantityOverrides(state) {
      dropSettledOverrides(state.quantityOverrides);
    },
    /**
     * Records a quantity edit next to the server carts rather than in them, so
     * the server value stays available to fall back on and a refetch can't drop
     * the edit. Keyed by cart, not looked up in `carts` — the cart page renders
     * its own query result and no longer mirrors it here.
     */
    applyCartItemQuantityOverride(
      state,
      action: PayloadAction<{
        cartId: string;
        productId: string;
        value: number;
        piecesCount?: number;
      }>
    ) {
      const { cartId, productId, value, piecesCount } = action.payload;
      (state.quantityOverrides[cartId] ??= {})[productId] = {
        value,
        piecesCount,
      };
    },
    /**
     * The save landed, so the next server payload supersedes this edit. The
     * value stays on screen until that payload arrives — clearing it here would
     * show the pre-edit quantity again for as long as the refetch takes.
     */
    settleCartItemQuantityOverride(
      state,
      action: PayloadAction<{ cartId: string; productId: string }>
    ) {
      const { cartId, productId } = action.payload;
      const override = state.quantityOverrides[cartId]?.[productId];
      if (!override) return;
      override.settled = true;
    },
    clearCartItemQuantityOverride(
      state,
      action: PayloadAction<{ cartId: string; productId: string }>
    ) {
      const { cartId, productId } = action.payload;
      dropOverride(state.quantityOverrides, cartId, productId);
    },
    removeCart(state, action: PayloadAction<string>) {
      state.carts = state.carts.filter((cart) => cart.id !== action.payload);
    },
    setCartTotalQuantity(state, action: PayloadAction<number>) {
      state.cartTotalQuantity = action.payload;
    },
    setSelectedLocationId(state, action: PayloadAction<string>) {
      state.selectedLocationId = action.payload;
    },
    setSellerDeliveryMethod(
      state,
      action: PayloadAction<{ key: string; method: DomesticDeliveryMethod }>
    ) {
      state.deliveryMethodBySeller[action.payload.key] = action.payload.method;
    },
    clearSellerDeliveryMethod(state, action: PayloadAction<string>) {
      delete state.deliveryMethodBySeller[action.payload];
    },
    incrementPendingQuantitySave(state) {
      state.pendingQuantitySaveCount += 1;
    },
    decrementPendingQuantitySave(state) {
      state.pendingQuantitySaveCount = Math.max(
        0,
        state.pendingQuantitySaveCount - 1
      );
    },
  },
});

export const {
  setCarts,
  dropSettledQuantityOverrides,
  removeCart,
  applyCartItemQuantityOverride,
  settleCartItemQuantityOverride,
  clearCartItemQuantityOverride,
  setCartTotalQuantity,
  setSelectedLocationId,
  setSellerDeliveryMethod,
  clearSellerDeliveryMethod,
  incrementPendingQuantitySave,
  decrementPendingQuantitySave,
} = cartSlice.actions;

export default cartSlice.reducer;
